import React, { useEffect, useState } from 'react';
import { useConnectedUser } from "../context/connected-user.context";
import { UserPage } from "./user-page";
import { isWeb3Supported } from "../web3/connectWallet";
import { MetaMaskSDK } from '@metamask/sdk';
import { ConnectWalletButton } from '../components/connect-wallet/connect-wallet';

export const Home: React.FC = () => {
    const [sdkInitialized, setSdkInitialized] = useState(false);
    const [sdkError, setSdkError] = useState<string | null>(null);
    const connectedUser = useConnectedUser().connectedUser;

    useEffect(() => {
        const initializeSDK = async () => {
            try {
                // Check if MetaMask is installed
                if (typeof window.ethereum === 'undefined') {
                    throw new Error('MetaMask is not installed');
                }

                // Initialize MetaMask SDK with more robust configuration
                const sdk = new MetaMaskSDK({
                    dappMetadata: {
                        name: 'CDK User Dapp',
                        url: window.location.origin,
                    },
                    injectProvider: true,
                    preferDesktop: false, // Allow mobile connections
                });

                // Wait for SDK to be ready with timeout
                const initTimeout = new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('SDK initialization timeout')), 10000)
                );

                await Promise.race([
                    sdk.init(),
                    initTimeout
                ]);
                
                setSdkInitialized(true);
            } catch (error) {
                console.error('Failed to initialize MetaMask SDK:', error);
                setSdkError(error instanceof Error ? error.message : 'Unknown SDK initialization error');
            }
        };

        // Check Web3 support and initialize SDK
        if (isWeb3Supported()) {
            initializeSDK();
        }
    }, []);

    // No Web3 support
    if (!isWeb3Supported()) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-900 text-white">
                <h2 className="text-2xl mb-4">Web3 Not Supported</h2>
                <p className="mb-4">
                    Your browser does not support Web3. 
                    Please use a modern browser with MetaMask extension.
                </p>
                <a 
                    href="https://metamask.io/download.html" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-400 hover:underline"
                >
                    Install MetaMask
                </a>
            </div>
        );
    }

    // SDK initialization error
    if (sdkError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white space-y-4 p-4">
                <h2 className="text-2xl mb-4">Wallet Connection Issue</h2>
                <p className="text-center">{sdkError}</p>
                <div className="flex flex-col space-y-2">
                    <a 
                        href="https://metamask.io/download.html" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-center"
                    >
                        Reinstall MetaMask
                    </a>
                    <button 
                        onClick={() => window.location.reload()}
                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                    >
                        Retry Connection
                    </button>
                </div>
                <details className="text-sm text-gray-300 max-w-md">
                    <summary>Troubleshooting Tips</summary>
                    <ul className="list-disc list-inside mt-2">
                        <li>Ensure MetaMask is installed and up to date</li>
                        <li>Check browser compatibility</li>
                        <li>Verify network connection</li>
                        <li>Restart your browser</li>
                    </ul>
                </details>
            </div>
        );
    }

    // Loading state while initializing SDK
    if (!sdkInitialized) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-900">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                    <div className="text-white">Initializing MetaMask SDK...</div>
                </div>
            </div>
        );
    }

    // Wallet not connected
    if (!connectedUser) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white">
                <h2 className="text-2xl mb-4">Connect Your Wallet</h2>
                <ConnectWalletButton autologin={true} />
            </div>
        );
    }

    // Wallet connected - show user page
    return <UserPage />;
};